






















































import { defineComponent, ref } from '@vue/composition-api';
import appFeedback from '@/components/common/Feedback.vue';
import soilApiService from '../../services/soilApiService';
import { Field } from '../../store/types';

const stratificationConfigPresets = [
	{
		name: 'Global',
		value: JSON.stringify(
			{
				inputs: [
					{
						source: 'SENTINEL_2',
						params: {
							dates: '2022-01-01/2022-12-31',
							masked_ratio_threshold: 0.3,
							ndvi_max: 1.0,
						},
					},
					{
						source: 'COPERNICUS',
						params: {},
					},
				],
				params: {
					sampling_densities: [
						{
							min_area: 0,
							max_area: 9999999999,
							units: 'HECTARES',
							density: 0.617763453667925,
							per: 'STRATUM',
						},
					],
					field_boundary_raster_mask_buffer: {
						type: 'DYNAMIC',
						buffer_max: -10,
						buffer_min: -30,
						buffer_interval: 5,
						threshold: 0.6,
					},
					bbox_padding_degrees: 0.001,
					n_strata: {
						type: 'DYNAMIC',
						value: [
							{
								min: 1,
								max: 5,
								rate: 0.2,
								min_area: 0,
								max_area: 9007199254740991,
								offset: 0,
							},
						],
						span: 4,
						pad_min_length: 5,
					},
				},
			},
			undefined,
			2
		),
	},
	{
		name: 'Continental United States',
		value: JSON.stringify(
			{
				inputs: [
					{
						source: 'SENTINEL_2',
						params: {
							dates: '2022-01-01/2022-12-31',
							masked_ratio_threshold: 0.3,
							ndvi_max: 1.0,
						},
					},
					{
						source: '3DEP',
						params: {},
					},
					{
						source: 'SSURGO',
						params: {},
					},
				],
				params: {
					sampling_densities: [
						{
							min_area: 0,
							max_area: 9999999999,
							units: 'HECTARES',
							density: 0.5,
							per: 'STRATUM',
						},
					],
					field_boundary_raster_mask_buffer: {
						type: 'DYNAMIC',
						buffer_max: -10,
						buffer_min: -30,
						buffer_interval: 5,
						threshold: 0.6,
					},
					bbox_padding_degrees: 0.001,
					n_strata: {
						type: 'DYNAMIC',
						value: [
							{
								min: 1,
								max: 5,
								rate: 0.2,
								min_area: 0,
								max_area: 9007199254740991,
								offset: 0,
							},
						],
						span: 4,
						pad_min_length: 5,
					},
				},
			},
			undefined,
			2
		),
	},
	{
		name: 'NDVI Demo',
		value: JSON.stringify(
			{
				inputs: [
					{
						source: 'COPERNICUS',
						params: {},
					},
				],
				params: {
					sampling_densities: [
						{
							min_area: 0,
							max_area: 9999999999,
							units: 'HECTARES',
							density: 0,
							per: 'STRATUM',
							min_samplings: 3,
						},
					],
					field_boundary_raster_mask_buffer: {
						type: 'STATIC',
						value: -10,
					},
					bbox_padding_degrees: 0.001,
					n_strata: {
						type: 'STATIC',
						value: 1,
					},
				},
			},
			undefined,
			2
		),
	},
	{
		name: 'Elevation Demo',
		value: JSON.stringify(
			{
				inputs: [
					{
						source: 'SENTINEL_2',
						params: {
							dates: '2022-01-01/2022-12-31',
							masked_ratio_threshold: 0.3,
							ndvi_max: 1.0,
						},
					},
				],
				params: {
					sampling_densities: [
						{
							min_area: 0,
							max_area: 9999999999,
							units: 'HECTARES',
							density: 0,
							per: 'STRATUM',
							min_samplings: 3,
						},
					],
					field_boundary_raster_mask_buffer: {
						type: 'STATIC',
						value: -10,
					},
					bbox_padding_degrees: 0.001,
					n_strata: {
						type: 'STATIC',
						value: 1,
					},
				},
			},
			undefined,
			2
		),
	},
];

const initForm = () => ({
	formRef: ref<any>(null),
	isFormValid: ref<boolean>(false),
	isLoading: ref<boolean>(false),
	errorMessage: ref<string>(''),
	selectedFields: ref<any[]>([]),
	stratificationConfigString: ref<string>(''),
	stratificationConfigPresetValue: ref<string>(''),
	autoApprove: ref<boolean>(false),
});

export default defineComponent({
	components: {
		appFeedback,
	},
	props: {
		initialFieldIds: {
			type: Array as () => string[],
			required: false,
		},
	},
	emits: ['submissionSuccess', 'cancel'],
	setup(props, { root, emit }) {
		const {
			formRef,
			isFormValid,
			isLoading,
			errorMessage,
			selectedFields,
			stratificationConfigString,
			stratificationConfigPresetValue,
			autoApprove,
		} = initForm();

		const fields = root.$store.getters['resources/getFields'];

		if (props.initialFieldIds) {
			selectedFields.value = props.initialFieldIds;
		}

		const validateJSON = (string: string) => {
			try {
				JSON.parse(string);
			} catch (e) {
				return (e as any).message;
			}
			return true;
		};
		const validateCombobox = (v: any) => v.length > 0 || 'You must select at least one field.';

		const resetForm = () => {
			isLoading.value = false;
			errorMessage.value = '';
			formRef.value.reset();
		};

		const handleCancel = (e: any) => {
			e.preventDefault();
			resetForm();
			emit('cancel');
		};

		const handleSubmit = async (e: any) => {
			e.preventDefault();
			isLoading.value = true;
			errorMessage.value = '';
			const areaIds = selectedFields.value.map(
				(id: string) => fields?.find((field: Field) => field.id === id).areas[0]
			);
			const stratificationConfig = JSON.parse(stratificationConfigString.value);
			try {
				await soilApiService.stratify(areaIds, stratificationConfig, autoApprove.value);
				resetForm();
				emit('submissionSuccess');
				return;
			} catch (error) {
				switch ((error as any)?.response?.status) {
					case 500:
						errorMessage.value =
							'The SoilStack API and/or Stratification Service is currently unavailable. Please try again later.';
						break;
					case 401:
						errorMessage.value = 'Unauthorized. Ensure you have the correct permissions.';
						break;
					case 404:
						errorMessage.value = 'One or more areas could not be found.';
						break;
					case 400:
						errorMessage.value = (error as any)?.response?.data?.message ?? 'Invalid Request.';
						break;
					default:
						errorMessage.value = 'There was an error submitting. Please try again.';
				}
			} finally {
				isLoading.value = false;
			}
		};

		return {
			errorMessage,
			formRef,
			isFormValid,
			selectedFields,
			fields,
			stratificationConfigPresets,
			stratificationConfigString,
			stratificationConfigPresetValue,
			autoApprove,
			validateJSON,
			validateCombobox,
			handleSubmit,
			isLoading,
			handleCancel,
		};
	},
});
